/**
 * Urban Element Colors Store — user-customisable colors per element type
 *
 * @module core/modules/urban/store/element-colors-store
 *
 * @description
 * Zustand store with localStorage persistence. Keeps only user overrides;
 * falls back to URBAN_ELEMENT_COLORS defaults for untouched entries.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { URBAN_ELEMENT_COLORS, type UrbanElementId } from '../constants/element-colors';

interface UrbanElementColorsState {
  /** Only colours the user has explicitly changed */
  overrides: Partial<Record<UrbanElementId, string>>;
}

interface UrbanElementColorsActions {
  /** Return override if set, otherwise default */
  getColor: (id: UrbanElementId) => string;
  /** Set a user override */
  setColor: (id: UrbanElementId, hex: string) => void;
  /** Remove a single override (revert to default) */
  resetColor: (id: UrbanElementId) => void;
  /** Remove all overrides */
  resetAll: () => void;
}

export type UrbanElementColorsStore = UrbanElementColorsState & UrbanElementColorsActions;

export const useUrbanElementColorsStore = create<UrbanElementColorsStore>()(
  devtools(
    persist(
      (set, get) => ({
        overrides: {},

        getColor(id: UrbanElementId): string {
          return get().overrides[id] ?? URBAN_ELEMENT_COLORS[id];
        },

        setColor(id: UrbanElementId, hex: string): void {
          set(
            (s) => ({ overrides: { ...s.overrides, [id]: hex } }),
            false,
            'setColor',
          );
        },

        resetColor(id: UrbanElementId): void {
          set(
            (s) => {
              const next = { ...s.overrides };
              delete next[id];
              return { overrides: next };
            },
            false,
            'resetColor',
          );
        },

        resetAll(): void {
          set({ overrides: {} }, false, 'resetAll');
        },
      }),
      { name: 'urban-element-colors' },
    ),
    { name: 'UrbanElementColors' },
  ),
);
